import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  FileText, 
  DollarSign, 
  Calendar, 
  Download, 
  Briefcase, 
  AlertCircle,
  RefreshCw,
  CheckCircle2,
  Inbox
} from 'lucide-react';

import { useToast, ToastContainer } from '../common/Toast';
import PageHeader from '../common/PageHeader';
import PageBreadcrumb from '../common/PageBreadcrumb';
import SourcingRfpService from '../../services/sourcingRFPService';

const ViewVendorBid = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error: showError, toasts, removeToast } = useToast();

  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(true);

  // Helper to get string ID safely
  const getStrId = (obj) => {
    if (!obj) return '';
    return typeof obj === 'string' ? obj : (obj._id || String(obj));
  };

  useEffect(() => {
    const fetchBidDetails = async () => {
      setLoading(true);
      try {
        // Fetch all responses - handling nested data property common in your API
        const res = await SourcingRfpService.getResponses('');
        const responseList = res.data?.data || res.data || [];

        // Match the response ID
        const found = responseList.find(r => getStrId(r) === id);

        if (found) {
          setResponse(found);
          console.log("Matched Bid Response:", found);
        } else {
          showError("Proposal not found.");
        }
      } catch (err) {
        showError("Failed to load bid details");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchBidDetails();
  }, [id]);

  if (loading) return (
    <div className="flex flex-col justify-center items-center h-screen bg-gray-50" style={{ color: 'var(--gold-primary)' }}>
      <RefreshCw className="animate-spin mb-4" size={40} />
      <p className="font-medium">Loading bid details...</p>
    </div>
  );
  if (!response) return (
    <div className="flex flex-col items-center justify-center h-screen text-gray-500">
      <AlertCircle size={48} className="text-red-500 mb-4" />
      <p className="text-lg font-semibold">Proposal Not Found</p>
      <button onClick={() => navigate(-1)} className="mt-4 px-6 py-2 rounded-lg text-white" style={{ backgroundColor: 'var(--gold-primary)' }}>Go Back</button>
    </div>
  );

  const totalValue = response.lineItems?.reduce((sum, item) => sum + (item.price || 0), 0) || 0;
  const rfp = response.rfpId;

  return (
    <div className="min-h-screen bg-[#fcfdfe]">
      <PageBreadcrumb />
      
      <PageHeader 
        title="View Vendor Bid"
        description={`Vendor: ${response.vendorId?.name || 'Unknown'}`}
        onBack={() => navigate(-1)}
        actions={[
          {
            label: 'Back to List',
            icon: <ArrowLeft size={18} />,
            variant: 'outline',
            onClick: () => navigate(-1)
          }
        ]}
      />

      <div className="w-full px-6 py-6 space-y-6">

        {/* --- HEADER SUMMARY CARDS --- */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-xl border shadow-sm" style={{ borderColor: 'var(--gold-primary)' }}>
            <div className="flex items-center gap-3 mb-2" style={{ color: 'var(--gold-primary)' }}>
              <DollarSign size={20} />
              <span className="text-xs font-bold uppercase tracking-widest">Total Proposal Value</span>
            </div>
            <p className="text-3xl font-bold text-gray-900">${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}</p>
          </div>

          <div className="bg-white p-6 rounded-xl border shadow-sm" style={{ borderColor: 'var(--gold-primary)' }}>
            <div className="flex items-center gap-3 mb-2" style={{ color: 'var(--gold-primary)' }}>
              <CheckCircle2 size={20} />
              <span className="text-xs font-bold uppercase tracking-widest">Submission Status</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-bold uppercase" style={{ backgroundColor: '#FFF9E6', color: 'var(--gold-primary)' }}>
                {response.status}
              </span>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl border shadow-sm" style={{ borderColor: 'var(--gold-primary)' }}>
            <div className="flex items-center gap-3 mb-2" style={{ color: 'var(--gold-primary)' }}>
              <Calendar size={20} />
              <span className="text-xs font-bold uppercase tracking-widest">Date Submitted</span>
            </div>
            <p className="text-lg font-semibold text-gray-800">
              {response.submittedAt ? new Date(response.submittedAt).toLocaleDateString(undefined, { dateStyle: 'long' }) : 'N/A'}
            </p>
          </div>
        </div>

        {/* --- RFP CONTEXT CARD --- */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden" style={{ borderColor: 'var(--gold-primary)' }}>
          <div className="px-6 py-4 border-b" style={{ backgroundColor: '#FFF9E6', borderColor: 'var(--gold-primary)' }}>
            <h3 className="font-bold text-sm uppercase tracking-wider flex items-center gap-2" style={{ color: 'var(--gold-primary)' }}>
              <Briefcase size={16} /> RFP Context
            </h3>
          </div>
          <div className="p-6 grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold text-gray-400 uppercase mb-1 block">RFP Title</label>
                <div className="text-md font-semibold text-gray-800 p-3 rounded-lg border bg-gray-50">
                  {rfp?.title || "Untitled RFP"}
                </div>
              </div>
              <div>
                <label className="text-[11px] font-bold text-gray-400 uppercase mb-1 block">Scope of Work</label>
                <div className="text-sm text-gray-600 p-3 rounded-lg border bg-gray-50 leading-relaxed min-h-[60px]">
                  {rfp?.scope || rfp?.scopeOfWork || "No scope details available."}
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-[11px] font-bold text-gray-400 uppercase mb-1 block">Vendor Notes / Cover Letter</label>
                <div className="text-sm text-gray-700 p-4 rounded-lg border-l-4 bg-[#fcfdfe] italic" style={{ borderLeftColor: 'var(--gold-primary)', backgroundColor: '#FFF9E6' }}>
                  {response.notes || "No notes provided by the vendor."}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- LINE ITEMS TABLE --- */}
        <div className="bg-white border rounded-xl shadow-sm overflow-hidden" style={{ borderColor: 'var(--gold-primary)' }}>
          <div className="px-6 py-4 border-b flex justify-between items-center" style={{ backgroundColor: '#fcfdfe' }}>
            <h3 className="font-bold text-gray-800 flex items-center gap-2">
              <div className="w-1 h-5 rounded-full" style={{ backgroundColor: 'var(--gold-primary)' }}></div>
              Commercial Proposal
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Item Description</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider text-right w-40">Unit Price</th>
                  <th className="p-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Vendor Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {response.lineItems?.map((item, idx) => {
                  // ROBUST MATCHING: Compare item.lineItemId with RFP lineItems IDs
                  const rfpItemMatch = rfp?.lineItems?.find(li =>
                    getStrId(li) === getStrId(item.lineItemId)
                  );

                  return (
                    <tr key={idx} className="hover:bg-gray-50 transition-colors">
                      <td className="p-4">
                        <p className="font-semibold text-gray-800">
                          {rfpItemMatch?.description || item.description || `Item #${idx + 1}`}
                        </p>
                        {rfpItemMatch?.requirement && (
                          <p className="text-xs text-gray-400 mt-0.5 italic">Req: {rfpItemMatch.requirement}</p>
                        )}
                      </td>
                      <td className="p-4 text-right">
                        <div className="inline-block px-3 py-1 rounded-md font-bold text-gray-900 bg-gray-50 border border-gray-100">
                          <span style={{ color: 'var(--gold-primary)' }}>$</span> {item.price?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </div>
                      </td>
                      <td className="p-4">
                        <p className="text-sm text-gray-600">
                          {item.notes || <span className="text-gray-300">No remarks.</span>}
                        </p>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2" style={{ borderColor: 'var(--gold-primary)', backgroundColor: '#fcfdfe' }}>
                  <td className="p-5 text-right font-bold text-gray-500 uppercase text-[11px]">Total Bid Amount:</td>
                  <td className="p-5 text-right font-black text-xl" style={{ color: 'var(--gold-primary)' }}>
                    ${totalValue.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className="p-5"></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* --- ATTACHMENTS --- */}
        <div className="bg-white p-6 rounded-xl border shadow-sm" style={{ borderColor: 'var(--gold-primary)' }}>
          <h3 className="font-bold text-gray-800 mb-4 flex items-center gap-2 text-sm uppercase tracking-widest">
            <FileText size={18} style={{ color: 'var(--gold-primary)' }} />
            Attachments
          </h3>
          {response.attachments?.length > 0 ? (
            <div className="flex flex-wrap gap-4">
              {response.attachments.map((file, i) => (
                <a 
                  key={i} 
                  href={file} 
                  target="_blank" 
                  rel="noreferrer"
                  className="flex items-center gap-3 p-3 border rounded-lg hover:shadow-md transition-all group"
                  style={{ borderColor: 'var(--gold-primary)' }}
                >
                  <Download size={16} style={{ color: 'var(--gold-primary)' }} />
                  <span className="text-sm font-medium text-gray-600 group-hover:text-blue-600">Document_{i + 1}.pdf</span>
                </a>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-100">
              <Inbox size={24} className="text-gray-300 mb-1" />
              <p className="text-xs text-gray-400">No documents submitted</p>
            </div>
          )}
        </div>
      </div>
      
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </div>
  );
};

export default ViewVendorBid;